import { DataGrid, GridColDef } from "@mui/x-data-grid";
import Paper from "@mui/material/Paper";
import { Box, Grid, Skeleton, Typography, useTheme } from "@mui/material";
import { StringDictionary } from "../../../types/stringDictionary";
import { getActivitiesStatus } from "../../../api/getOverviewData";
import { useEffect, useState } from "react";
import { ActivityStatus } from "../../../types/activityStatus";

const statusColorDict: StringDictionary = {
  0: "rgba(75, 192, 192, 1)",
  1: "rgba(255, 206, 86, 1)",
  2: "rgba(255, 99, 132, 1)",
};

const statusTextDict: StringDictionary = {
  0: "Completed",
  1: "Not Enough Data",
  2: "Not Registered",
};

const columns: GridColDef[] = [
  { field: "activity", headerName: "Activity Name", width: 300 },
  { field: "pc", headerName: "Project Code", width: 100 },
  { field: "users", headerName: "Users", width: 70 },
  {
    field: "status",
    headerName: "Status",
    width: 150,
    renderCell: (params) => (
      <Box display="flex" alignItems="center">
        <Box
          component="span"
          sx={{
            height: 10,
            width: 10,
            borderRadius: "50%",
            backgroundColor: statusColorDict[params.value],
            marginRight: 1,
          }}
        />
        {statusTextDict[params.value]}
      </Box>
    ),
  },
];

const rows = [
  { id: 1, activity: "Packaging", pc: "PC1", status: 1, users: 2 },
  { id: 2, activity: "Computer Work", pc: "PC2", status: 0, users: 3 },
  { id: 3, activity: "Truck Driving", pc: "PC3", status: 0, users: 4 },
  { id: 4, activity: "Cleaning Surfaces", pc: "PC4", status: 0, users: 5 },
  { id: 5, activity: "Forklift Driving", pc: "PC5", status: 2, users: 6 },
  { id: 6, activity: "Lunch Break", pc: "PC6", status: 0, users: 7 },
  { id: 7, activity: "Sport", pc: "PC7", status: 0, users: 8 },
  { id: 8, activity: "Lifting Packages", pc: "PC8", status: 0, users: 9 },
  { id: 9, activity: "Manual Transportation", pc: "PC9", status: 0, users: 10 },
  { id: 10, activity: "Meeting", pc: "PC10", status: 0, users: 11 },
];

const paginationModel = { page: 0, pageSize: 8 };

type ActivitiesTableProps = {
  projectCodes: string[];
  height: number;
};

export default function ActivitiesTable({
  projectCodes,
  height,
}: ActivitiesTableProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const theme = useTheme();

  const statusColorDict: StringDictionary = {
    0: theme.palette.primaryChart,
    1: theme.palette.terciaryChart,
    2: theme.palette.secondaryChart,
  };

  const statusTextDict: StringDictionary = {
    0: "Active",
    1: "Not Enough Data",
    2: "Not Registered",
  };

  const columns: GridColDef[] = [
    {
      field: "activity",
      headerName: "Activity Name".toUpperCase(),
      width: 300,
    },
    {
      field: "projectCode",
      headerName: "Project Code".toUpperCase(),
      width: 150,
    },
    { field: "users", headerName: "Users".toUpperCase(), width: 70 },
    {
      field: "status",
      headerName: "Status".toUpperCase(),
      width: 150,
      renderCell: (params) => (
        <Box display="flex" alignItems="center">
          <Box
            component="span"
            sx={{
              height: 10,
              width: 10,
              borderRadius: "50%",
              backgroundColor: statusColorDict[params.value],
              marginRight: 1,
            }}
          />
          {statusTextDict[params.value]}
        </Box>
      ),
    },
  ];

  useEffect(() => {
    setIsLoading(true);
    if (projectCodes.length === 0) return;
    let controller = new AbortController();
    let isMounted = true;
    const fetchUsersPerDay = async () => {
      try {
        const data = await getActivitiesStatus(projectCodes, controller);
        if (isMounted) {
          if (data.length > 0) {
            const rows = data.map((value: ActivityStatus, index: number) => ({
              id: index,
              activity: value.name,
              projectCode: value.projectCode,
              users: value.users,
              status: value.status,
            }));
            setRows(rows);
          }
        }
      } catch (error: any) {
        if (error.name === "AbortError") {
          console.log("Fetch aborted");
        } else {
          console.error("Fetch error:", error);
        }
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    fetchUsersPerDay();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [projectCodes]);

  return rows.length > 0 && isLoading === false ? (
    <Paper sx={{ borderRadius: "5px", height: height, padding: "0 10px" }}>
          <DataGrid
            rows={rows}
            columns={columns}
            initialState={{ pagination: { paginationModel } }}
            sx={{
              border: 0,
              display: "grid",
              gridTemplateRows: "auto 1f auto",
              borderRadius: "5px",
              "& .MuiDataGrid-columnHeaders": {
                color: theme.palette.text.secondary, // Header text color
              },
              "& .MuiDataGrid-cell": {
                color: theme.palette.text.primary,
              },
              "&, [class^=MuiDataGrid]": { border: "none" },
              //"--DataGrid-rowBorderColor": "none", // Ensures no row borders are rendered
            }}
          />
    </Paper>
  ) : (
    <Skeleton
      sx={{
        borderRadius: "5px",
        boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.2)",
      }}
      animation="pulse"
      variant="rectangular"
      width={"100%"}
      height={height}
    />
  );
}
